import dayjs from 'dayjs';

const toTimestamp = (value) => {
    if (typeof value === 'number') {
        return value;
    }

    return new Date(value).valueOf();
};

const toFiniteNumeric = (value) => {
    const numericValue = Number.parseFloat(value);

    return Number.isFinite(numericValue) ? numericValue : Number.NaN;
};

export const CHART_DATA_SMOOTHING = {
    DAILY: 'DAILY',
    WEEKLY: 'WEEKLY',
    MONTHLY: 'MONTHLY',
};

export const CHART_DATA_SMOOTHING_OPTIONS = [
    { value: CHART_DATA_SMOOTHING.DAILY, label: 'Daily' },
    { value: CHART_DATA_SMOOTHING.WEEKLY, label: 'Weekly' },
    { value: CHART_DATA_SMOOTHING.MONTHLY, label: 'Monthly' },
];

const average = (values) => {
    if (!values.length) {
        return null;
    }

    return values.reduce((sum, value) => sum + value, 0) / values.length;
};

/**
 * @param {number} timestamp
 * @param {string} smoothing
 * @returns {number}
 */
export const getBucketStartTimestamp = (timestamp, smoothing) => {
    const date = dayjs(timestamp);

    if (smoothing === CHART_DATA_SMOOTHING.WEEKLY) {
        return date.startOf('week').valueOf();
    }

    if (smoothing === CHART_DATA_SMOOTHING.MONTHLY) {
        return date.startOf('month').valueOf();
    }

    return timestamp;
};

/**
 * @param {Array<number>} timestamps
 * @returns {{ min: number, max: number }|null}
 */
export const getChartDataXAxisRange = (timestamps) => {
    const valid = (timestamps || []).filter((timestamp) => Number.isFinite(timestamp));

    if (!valid.length) {
        return null;
    }

    return {
        min: Math.min(...valid),
        max: Math.max(...valid),
    };
};

/**
 * @param {Array<Object>} points
 * @param {function(Object): number} getTimestamp
 * @returns {{ min: number, max: number }|null}
 */
export const getChartDataXAxisRangeFromPoints = (points, getTimestamp) => {
    if (!Array.isArray(points) || !points.length) {
        return null;
    }

    return getChartDataXAxisRange(points.map(getTimestamp));
};

/**
 * @param {Array<Object>} data
 * @param {string} smoothing
 * @param {Object} config
 * @returns {Array<Object>}
 */
export const aggregatePointsBySmoothing = (data, smoothing, config) => {
    const {
        getTimestamp,
        getValue,
        getSeriesId,
        buildPoint,
    } = config;

    if (!smoothing
        || smoothing === CHART_DATA_SMOOTHING.DAILY
        || !Array.isArray(data)
        || !data.length) {
        return data || [];
    }

    const buckets = new Map();

    data.forEach((point) => {
        const timestamp = getTimestamp(point);
        const value = getValue(point);

        if (!Number.isFinite(timestamp) || !Number.isFinite(value)) {
            return;
        }

        const seriesId = getSeriesId(point);
        const bucketStart = getBucketStartTimestamp(timestamp, smoothing);
        const key = `${seriesId}::${bucketStart}`;

        if (!buckets.has(key)) {
            buckets.set(key, {
                point,
                bucketStart,
                values: [],
            });
        }

        buckets.get(key).values.push(value);
    });

    return [...buckets.values()]
        .map(({ point, bucketStart, values }) => {
            const mean = average(values);

            if (!Number.isFinite(mean)) {
                return null;
            }

            return buildPoint(point, bucketStart, mean);
        })
        .filter(Boolean)
        .sort((left, right) => getTimestamp(left) - getTimestamp(right));
};

/**
 * @param {Array<Object>} data
 * @param {string} direction
 * @param {string} smoothing
 * @param {string} valueKey
 * @returns {{ data: Array<Object>, xAxisRange: { min: number, max: number }|null }}
 */
export const prepareSnapshotChartData = (data, direction, smoothing, valueKey) => {
    const filtered = direction
        ? (data || []).filter((item) => item.direction === direction)
        : (data || []);

    const xAxisRange = getChartDataXAxisRangeFromPoints(
        filtered,
        (point) => toTimestamp(point.snapshot_dt),
    );

    const displayData = aggregatePointsBySmoothing(filtered, smoothing, {
        getTimestamp: (point) => toTimestamp(point.snapshot_dt),
        getValue: (point) => toFiniteNumeric(point[valueKey]),
        getSeriesId: (point) => point.direction,
        buildPoint: (point, bucketStart, mean) => ({
            ...point,
            snapshot_dt: new Date(bucketStart).toISOString(),
            [valueKey]: mean,
        }),
    });

    return { data: displayData, xAxisRange };
};

/**
 * @param {Array<Object>} data
 * @param {string} direction
 * @param {string} smoothing
 * @returns {{ data: Array<Object>, xAxisRange: { min: number, max: number }|null }}
 */
export const prepareNamedLineChartData = (data, direction, smoothing) => {
    const filtered = direction
        ? (data || []).filter((item) => item.name === direction)
        : (data || []);

    const xAxisRange = getChartDataXAxisRangeFromPoints(
        filtered,
        (point) => toTimestamp(point.x),
    );

    const displayData = aggregatePointsBySmoothing(filtered, smoothing, {
        getTimestamp: (point) => toTimestamp(point.x),
        getValue: (point) => toFiniteNumeric(point.y),
        getSeriesId: (point) => point.name,
        buildPoint: (point, bucketStart, mean) => ({
            ...point,
            x: bucketStart,
            y: mean,
        }),
    });

    return { data: displayData, xAxisRange };
};

/**
 * @param {Array<Array<Object>>} data
 * @param {string} direction
 * @param {string} smoothing
 * @returns {{ data: Array<Array<Object>>, xAxisRange: { min: number, max: number }|null }}
 */
export const prepareNestedTripDurationChartData = (data, direction, smoothing) => {
    const filteredDays = (data || [])
        .map((dayItem) => (
            Array.isArray(dayItem)
                ? dayItem.filter((item) => !direction || item.direction === direction)
                : (!direction || dayItem?.direction === direction ? dayItem : null)
        ))
        .filter((dayItem) => (
            Array.isArray(dayItem) ? dayItem.length > 0 : Boolean(dayItem)
        ));

    const flatPoints = filteredDays.flatMap((dayItem) => (
        Array.isArray(dayItem) ? dayItem : [dayItem]
    )).filter(Boolean);

    const xAxisRange = getChartDataXAxisRangeFromPoints(
        flatPoints,
        (point) => toTimestamp(point.date),
    );

    if (!smoothing || smoothing === CHART_DATA_SMOOTHING.DAILY) {
        return { data: filteredDays, xAxisRange };
    }

    const aggregatedPoints = aggregatePointsBySmoothing(flatPoints, smoothing, {
        getTimestamp: (point) => toTimestamp(point.date),
        getValue: (point) => toFiniteNumeric(point.avg_trip_duration_seconds),
        getSeriesId: (point) => point.direction,
        buildPoint: (point, bucketStart, mean) => ({
            ...point,
            date: new Date(bucketStart).toISOString(),
            avg_trip_duration_seconds: mean,
        }),
    });

    return {
        data: aggregatedPoints.map((point) => [point]),
        xAxisRange,
    };
};

/**
 * @param {{ min: number, max: number }} axisValues
 * @param {{ min: number, max: number }|null} xAxisRange
 * @returns {{ min: number, max: number }}
 */
export const applyXAxisRangeToAxisValues = (axisValues, xAxisRange) => {
    if (!xAxisRange) {
        return axisValues;
    }

    return {
        ...axisValues,
        x: {
            ...axisValues.x,
            min: xAxisRange.min,
            max: xAxisRange.max,
        },
    };
};
